import {
  createContext,
  createMemo,
  createSignal,
  ParentComponent,
  Accessor,
  Setter,
  useContext,
  createResource,
} from "solid-js";
import { MONTH_NAMES } from "@/shared/constants/date.const";
import { fetchPricing } from "@/services/checkout.service";
import { isPreviousMonth } from "@/utils/checkout.utils";
import { CancelCampaignParams } from "@/Main";

export enum CheckoutStep {
  CAMPAIGN_LENGTH = "CAMPAIGN_LENGTH",
  REVIEW = "REVIEW",
  CANCEL = "CANCEL",
}

export const CHECKOUT_STEP_TITLES: Record<CheckoutStep, string> = {
  [CheckoutStep.CAMPAIGN_LENGTH]: "Campaign Length",
  [CheckoutStep.REVIEW]: "Final Review",
  [CheckoutStep.CANCEL]: "Details",
};

export const CHECKOUT_STEP_DESCRIPTIONS: Partial<Record<CheckoutStep, string>> =
  {
    [CheckoutStep.CAMPAIGN_LENGTH]:
      "Get your inventory noticed with premium search placement on Auto Navigator. Connect with high-intent buyers through top-of-page results in your area.",
    [CheckoutStep.REVIEW]:
      "Your inventory will have premium search placement on Auto Navigator. Allowing you to connect with high-intent buyers in your area.",
  };

export interface CampaignSelection {
  month: string;
  year: string;
  price: number;
}

export interface CheckoutFlowContextType {
  // Dealer info
  dealerId: Accessor<string>;
  cancelCampaign: Accessor<CancelCampaignParams | undefined>;
  featureToggles: Accessor<Record<string, boolean> | undefined>;

  // Navigation
  currentStep: Accessor<CheckoutStep>;
  setCurrentStep: Setter<CheckoutStep>;

  // Selections
  selections: Accessor<CampaignSelection[]>;
  setSelections: Setter<CampaignSelection[]>;

  // Computed values
  totalPrice: Accessor<number>;

  // Modal state
  showOrderConfirmation: Accessor<boolean>;
  setShowOrderConfirmation: Setter<boolean>;
  showGlobalError: Accessor<boolean>;
  setShowGlobalError: Setter<boolean>;

  // Submission state
  isSubmitting: Accessor<boolean>;
  setIsSubmitting: Setter<boolean>;

  // Actions
  exitFeaturedDealer: () => void;
}

const CheckoutFlowContext = createContext<CheckoutFlowContextType>();

interface CheckoutFlowProviderProps {
  dealerId: string;
  featureToggles?: Record<string, boolean>;
  cancelCampaign?: CancelCampaignParams;
  exitFeaturedDealer: () => void;
}

export const CheckoutFlowProvider: ParentComponent<
  CheckoutFlowProviderProps
> = (props) => {
  const dealerId = () => props.dealerId;
  const cancelCampaign = () => props.cancelCampaign;
  const featureToggles = () => props.featureToggles;

  const initialStep = props.cancelCampaign
    ? CheckoutStep.CANCEL
    : CheckoutStep.CAMPAIGN_LENGTH;

  const [currentStep, setCurrentStep] = createSignal<CheckoutStep>(initialStep);
  const [selections, setSelections] = createSignal<CampaignSelection[]>([]);
  const [showOrderConfirmation, setShowOrderConfirmation] =
    createSignal<boolean>(false);
  const [showGlobalError, setShowGlobalError] = createSignal<boolean>(false);
  const [isSubmitting, setIsSubmitting] = createSignal<boolean>(false);

  // Skip the pricing fetch for previous months — their price is not
  // displayed, so it must not be fetched.
  const [pricingData] = createResource(
    () => {
      const cancel = cancelCampaign();
      if (!cancel || isPreviousMonth(cancel.month, cancel.year)) {
        return null;
      }
      return dealerId();
    },
    (id) => (id ? fetchPricing(id) : Promise.resolve(undefined)),
  );

  // By using the selections signal on the CANCEL screen, we're able to have
  // flexibility for the future to cancel multiple campaigns at once
  createMemo(() => {
    const cancel = cancelCampaign();
    if (!cancel || selections().length > 0) return;

    const isPrevious = isPreviousMonth(cancel.month, cancel.year);
    const pricing = pricingData();

    // For current/future campaigns, wait until pricing resolves
    if (!isPrevious && !pricing) return;

    const selection: CampaignSelection = {
      month: MONTH_NAMES[cancel.month],
      year: cancel.year.toString(),
      price: isPrevious ? 0 : pricing!.featuredDealerBasePrice,
    };
    setSelections([selection]);
  });

  const totalPrice = createMemo(() =>
    selections().reduce((sum, selection) => sum + selection.price, 0),
  );

  const context: CheckoutFlowContextType = {
    dealerId,
    cancelCampaign,
    featureToggles,
    currentStep,
    setCurrentStep,
    selections,
    setSelections,
    totalPrice,
    showOrderConfirmation,
    setShowOrderConfirmation,
    showGlobalError,
    setShowGlobalError,
    isSubmitting,
    setIsSubmitting,
    exitFeaturedDealer: props.exitFeaturedDealer,
  };

  return (
    <CheckoutFlowContext.Provider value={context}>
      {props.children}
    </CheckoutFlowContext.Provider>
  );
};

export const useCheckoutFlow = () => {
  const context = useContext(CheckoutFlowContext);

  if (!context) {
    throw new Error("useCheckoutFlow must be used within CheckoutFlowProvider");
  }

  return context;
};
